/**
 * Les gestes de « Libérer des créneaux » : l'aperçu (`previewSlotRelease`, qui n'écrit rien) et la
 * libération (`releaseSlots`, la seule écriture, à « Confirmer »).
 *
 * Port de `ReleaseSlotsScreen.js` :
 *  - « Suivant » demande l'aperçu de la sélection ; « J'arrête tous mes créneaux » celui de tout
 *    arrêter (motif « unavailable ») ; quand l'aperçu peut coûter des élèves, « Tu risques de
 *    perdre… » s'ouvre avant « Tes élèves » ;
 *  - « Ajouter le mardi à 17h » (un créneau de sa famille, pour garder un élève) agrandit la semaine
 *    et redemande l'aperçu, sans quitter « Tes élèves » ;
 *  - « Confirmer » n'envoie rien pendant un aperçu, sans aperçu, ou sur un refus ; ses engagements
 *    ponctuels sur les créneaux rendus (remplacements, essais) sont annulés avec (`cancel_one_offs`) ;
 *    après, le compte et les lives sont relus (l'accueil lit ses lives sur le compte) ;
 *  - la croix sort vers « Ma dispo » ; « Revenir à Ma dispo » revient d'où l'on vient — depuis un live,
 *    deux écrans plus bas (le parcours d'absence, dessous, parle d'un live qui s'arrête).
 * Mesuré : `availability.release` (ouvert avec le parcours). L'aperçu n'est pas un geste.
 */
import { useNavigation } from "@react-navigation/native"
import { useEffect, useReducer, useRef } from "react"
import { useDispatch } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { authService } from "../../../../../../data/services/authService"
import { liveService } from "../../../../../../data/services/liveService"
import { tutorAvailabilityService } from "../../../../../../data/services/tutorAvailabilityService"
import {
    type ApiReleasePreview,
    buildReleaseBody,
    RELEASE_MOTIF,
    type ReleaseMotif,
    studentsAtRiskCount,
} from "../../../../adapters/tutor/Availability/tutorReleaseAdapter"
import { withSlot } from "../../../../adapters/tutor/shared/tutorWeekAdapter"
import { type TutorReleaseSlotsViewModel } from "../../../../view-models/tutor/Availability/tutorReleaseSlotsViewModel"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { useBackOr } from "../../../hooks/useBackOr"
import { type UITutorHour, type UITutorReleaseSuggestedSlot } from "../../../types/tutorDispoOffres"
import {
    initialTutorReleaseSlotsUiState,
    tutorReleaseSlotsUiReducer,
    type TutorReleaseSlotsUiAction,
    type TutorReleaseSlotsUiState,
} from "./tutorReleaseSlotsUiState"

const SURFACE = "tutor_release_slots"

const PREVIEW_FALLBACK = "Impossible de calculer ce qui change pour tes élèves. Réessaie dans un instant."
const RELEASE_FALLBACK = "Tes créneaux n'ont pas pu être libérés. Réessaie dans un instant."

type ReleaseNavigation = {
    canGoBack: () => boolean
    goBack: () => void
    pop: (count: number) => void
    replace: (route: string) => void
}

/** Ce que la route dit du parcours : le créneau ou le live d'où il part. */
export type TutorReleaseSlotsParams = {
    slot: string | null
    liveSessionId: number | string | null
    oneOffLabel: string | null
}

/** Le brouillon et l'étape, ouverts sur le créneau passé par la route. */
export function useTutorReleaseSlotsUi(slot: string | null) {
    return useReducer(tutorReleaseSlotsUiReducer, slot, initialTutorReleaseSlotsUiState)
}

export function useTutorReleaseSlotsState(
    vm: TutorReleaseSlotsViewModel,
    ui: TutorReleaseSlotsUiState,
    dispatchUi: (action: TutorReleaseSlotsUiAction) => void,
    params: TutorReleaseSlotsParams
) {
    const navigation = useNavigation() as unknown as ReleaseNavigation
    const dispatch = useDispatch()
    const exit = useBackOr("tutor/ma-dispo")
    // Un essai ou un stage ne tient aucun créneau de la semaine : le serveur rend l'heure du live.
    const oneOffLiveIds = params.oneOffLabel != null && params.liveSessionId != null ? [params.liveSessionId] : []

    const body = (overrides: { motif?: ReleaseMotif; draft?: string[] | null } = {}) =>
        buildReleaseBody({
            motif: overrides.motif ?? ui.motif,
            selectedSlots: vm.selected,
            serverOpenSlots: vm.serverOpenSlots,
            draftOpenSlots: overrides.draft !== undefined ? overrides.draft : ui.draftOpenSlots,
            oneOffLiveIds,
        })

    const showError = (message: string) => dispatchUi({ type: "SHOW_ERROR", message })

    // Un aperçu demandé depuis « Tes élèves » (un créneau ouvert) reste sur cette étape.
    const advanceOnPreviewRef = useRef(false)
    const preview = useApiMutation({
        service: tutorAvailabilityService.previewSlotRelease,
        // Un aperçu n'écrit rien : ce n'est pas un geste de la personne.
        gesture: false,
        fallback: PREVIEW_FALLBACK,
        onSuccess: (data) => {
            if (!advanceOnPreviewRef.current) return
            const answer = data as ApiReleasePreview | null
            const atRisk = answer?.refusal ? 0 : studentsAtRiskCount(answer)
            if (atRisk > 0) dispatchUi({ type: "WARN_LOSS", count: atRisk })
            else dispatchUi({ type: "GO_STEP", step: "students" })
        },
        onError: showError,
    })
    const release = useApiMutation({
        service: tutorAvailabilityService.releaseSlots,
        gesture: "availability.release",
        surface: SURFACE,
        fallback: RELEASE_FALLBACK,
        skipWhilePending: true,
        onSuccess: () => {
            // L'accueil lit les lives du tuteur sur le compte.
            dispatch(apiRequest(authService.getUser) as never)
            dispatch(apiRequest(liveService.getLiveSessions) as never)
            dispatchUi({ type: "GO_STEP", step: "done" })
        },
        onError: showError,
    })

    // Le parcours est la feuille du geste : il s'ouvre avec l'écran.
    const releaseGesture = release.gesture
    useEffect(() => {
        releaseGesture.opened()
        // Une fois, à l'ouverture.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const requestPreview = (options: { advance?: boolean; motif?: ReleaseMotif; draft?: string[] | null } = {}) => {
        const { advance = true, ...overrides } = options
        advanceOnPreviewRef.current = advance
        dispatchUi({ type: "ASK_PREVIEW", motif: overrides.motif ?? ui.motif })
        preview.start({}, body(overrides))
    }

    const previewing = preview.requesting
    const stopping = ui.motif === RELEASE_MOTIF.UNAVAILABLE

    return {
        onPressHour: (hour: UITutorHour) =>
            dispatchUi({ type: "PRESS_HOUR", hour, serverOpenSlots: vm.serverOpenSlots }),
        onContinue: () => requestPreview({ motif: RELEASE_MOTIF.SCHEDULE_CHANGE }),
        continuing: !stopping && previewing,
        onStopAll: () => requestPreview({ motif: RELEASE_MOTIF.UNAVAILABLE }),
        stoppingAll: stopping && previewing,
        onOpenSlot: (slot: UITutorReleaseSuggestedSlot) => {
            const draft = withSlot(ui.draftOpenSlots ?? vm.serverOpenSlots, slot.id)
            dispatchUi({ type: "SET_DRAFT", draft })
            requestPreview({ advance: false, draft })
        },
        opening: ui.step === "students" && previewing,
        onConfirm: () => {
            if (release.requesting || previewing || vm.preview == null || vm.preview.refusal != null) return
            release.start({}, { ...body(), cancel_one_offs: true })
        },
        confirming: release.requesting,
        fromLive: params.liveSessionId != null,
        onDone: () => {
            if (params.liveSessionId != null && navigation.canGoBack()) navigation.pop(2)
            else if (navigation.canGoBack()) navigation.goBack()
            else navigation.replace("tutor/ma-dispo")
        },
        onBack: () => dispatchUi({ type: "GO_STEP", step: "week" }),
        onClose: exit,
        dialog:
            ui.lossCount != null
                ? {
                      visible: true,
                      kind: "releaseLoss" as const,
                      count: ui.lossCount,
                      // « Revenir » : on reste sur la semaine.
                      onClose: () => dispatchUi({ type: "CLOSE_LOSS" }),
                      onContinue: () => dispatchUi({ type: "GO_STEP", step: "students" }),
                  }
                : null,
        errorMessage: ui.errorMessage,
        onDismissError: () => dispatchUi({ type: "DISMISS_ERROR" }),
    }
}
